import React, { useContext, useEffect, useRef } from 'react';

import {
  Box,
  Button,
  Input,
  Text,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Spinner,
  Link,
  Flex,
  Checkbox,
} from '@chakra-ui/react';

import { AttachmentIcon, DeleteIcon } from '@chakra-ui/icons';

import { useAppDispatch } from 'hooks';

import { useMutation, useQuery } from '@tanstack/react-query';

import {
  apiCreateApplicationFile,
  apiCreateApplicationFileValidatorLog,
  apiApplicationFileValidatorLog,
} from 'api';

import {
  updateFile as updateFileAuto,
  removeSubFile as removeSubFileAuto,
  setFileDeferredStatus as setFileDeferredStatusAuto,
} from 'reducers/autoPersonalFormReducer';

import {
  updateFile as updateFileHome,
  removeSubFile as removeSubFileHome,
  setFileDeferredStatus as setFileDeferredStatusHome,
} from 'reducers/homePersonalFormReducer';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import isDeferralAllowed from 'helpers/isDeferralAllowed';
import { useHasUnderwritingApproval } from 'hooks/useHasUnderwritingApproval';
import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import type {
  APISubmissionFileData,
  APIRemarkFile,
  APISubmissionFileValidatorLogData,
} from 'types/api.types';

const ALLOWED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'text/plain': ['.txt'],
};

const MARKET_APPROVAL_EMAIL_FILE_TYPES = {
  'message/rfc822': ['.eml'],
  'application/vnd.ms-outlook': ['.msg'],
};

const OWNERSHIP_DEFERRABLE_VALIDATIONS = ['vehicle_ownership', '097'];

const MARKET_APPROVAL_FILE_TYPE_IDS = new Set([
  36, // TYPE_MARKET_APPROVAL_PROPERTY
  37, // TYPE_MARKET_APPROVAL_AUTO
]);

const DEFAULT_ACCEPT_STRING = Object.entries(ALLOWED_FILE_TYPES)
  .map(([mime, exts]) => `${mime},${exts.join(',')}`)
  .join(',');

const MARKET_APPROVAL_EMAIL_ACCEPT_STRING = '.eml,.msg';

const ValidateFile = ({
  uuid,
  file,
  validationId,
  itemKey,
  formStateKey,
  context,
}: {
  uuid: string;
  file: APIRemarkFile;
  validationId: string;
  itemKey: string;
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
  context?: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const dispatch = useAppDispatch();
  const isBinderDisabled = useFormBinderDisabled(formStateKey);
  const isFileTypeValidationEnabled = useFeatureFlag(
    'enable-file-type-validation',
  );
  const isMarketApprovalEmailEmlMsgEnabled = useFeatureFlag(
    'enable-market-approval-email-eml-msg',
  );
  const isAiContextEnabled = useFeatureFlag('enable-ai-remark-context');
  const isOwnershipOnlyDeferralEnabled = useFeatureFlag(
    'enable-ownership-only-deferral',
  );
  const hasUnderwritingApproval = useHasUnderwritingApproval(formStateKey);
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const isMarketApprovalEmailUpload =
    isMarketApprovalEmailEmlMsgEnabled &&
    MARKET_APPROVAL_FILE_TYPE_IDS.has(file.type);
  const allowedFileTypes = isMarketApprovalEmailUpload
    ? MARKET_APPROVAL_EMAIL_FILE_TYPES
    : ALLOWED_FILE_TYPES;
  const acceptString = isMarketApprovalEmailUpload
    ? MARKET_APPROVAL_EMAIL_ACCEPT_STRING
    : DEFAULT_ACCEPT_STRING;

  const updateFileReducer =
    formStateKey === 'autoPersonalForm' ? updateFileAuto : updateFileHome;
  const removeSubFileReducer =
    formStateKey === 'autoPersonalForm' ? removeSubFileAuto : removeSubFileHome;
  const setFileDeferredStatusReducer =
    formStateKey === 'autoPersonalForm'
      ? setFileDeferredStatusAuto
      : setFileDeferredStatusHome;
  const isMandatoryValidation = ['127', '132', '133', '133a', '133b'].includes(
    validationId,
  );

  const {
    mutate: createApplicationFile,
    isPending: isUploadingFile,
    error: uploadingFileError,
  } = useMutation({
    mutationFn: (formData: FormData) =>
      apiCreateApplicationFile(uuid, formData),
    onSuccess: (data: APISubmissionFileData) => {
      toast({
        title: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      dispatch(
        updateFileReducer({
          validation_key: validationId,
          item_key: itemKey,
          new_file: {
            name: data.name,
            file: data.file,
            id: data.id,
            created_at: data.created_at,
          },
          status: 'created',
        }),
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || 'Failed to upload file';
      toast({
        title: 'Upload failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const { mutate: createFileValidatorLog, isPending: isPendingValidatorLog } =
    useMutation({
      mutationFn: () =>
        apiCreateApplicationFileValidatorLog(uuid, {
          validation_key: validationId,
          item_key: itemKey,
          type: file.type,
          files: file.files.map((f) => f.id),
          ...(isAiContextEnabled && context ? { context } : {}),
        }),
      onSuccess: (data) => {
        dispatch(
          updateFileReducer({
            validation_key: validationId,
            item_key: itemKey,
            file_validator_log_id: data.file_validator_log_id,
            status: data.file_validator_log_id ? 'validating' : 'approved',
          }),
        );
      },
    });

  const { data: validatorLog } = useQuery({
    queryKey: ['validatorLog', uuid, file.file_validator_log_id],
    queryFn: () =>
      apiApplicationFileValidatorLog(uuid, file.file_validator_log_id),
    enabled: file.status === 'validating',
    refetchInterval: (query) => {
      const data = query.state.data as APISubmissionFileValidatorLogData;
      if (!data) return 2000;
      if (data.status !== 'validating') return false;
      return 2000;
    },
  });

  useEffect(() => {
    if (validatorLog && validatorLog.status !== 'validating') {
      dispatch(
        updateFileReducer({
          validation_key: validationId,
          item_key: itemKey,
          ai_result: validatorLog.result,
          status: validatorLog.status,
        }),
      );
    }
  }, [validatorLog, dispatch, validationId, itemKey]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadFile = event.target.files?.[0];
    if (!file) return;
    if (!uploadFile) return;

    if (isFileTypeValidationEnabled || isMarketApprovalEmailUpload) {
      const isAllowed =
        Object.keys(allowedFileTypes).includes(uploadFile.type) ||
        Object.values(allowedFileTypes)
          .flat()
          .some((ext) => uploadFile.name.toLowerCase().endsWith(ext));

      if (!isAllowed) {
        toast({
          title: 'Invalid file type',
          description: isMarketApprovalEmailUpload
            ? 'Only .eml and .msg files are allowed for market approval email.'
            : 'Only PDF, JPEG, PNG, WebP, and text files are allowed.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('type', file.type.toString());
    formData.append('validation', validationId);
    formData.append('item_key', itemKey);

    createApplicationFile(formData);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getAlertStatus = (remark: APIRemarkFile) => {
    if (remark.is_deferred) return 'success';
    return remark.status === 'approved' ? 'success' : 'error';
  };

  const onFileRemove = (id: number) => {
    dispatch(
      removeSubFileReducer({
        validation_key: file.validation_key,
        item_key: file.item_key,
        id,
      }),
    );
  };

  const handleDeferredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isDeferred = e.target.checked;

    dispatch(
      setFileDeferredStatusReducer({
        validation_key: validationId,
        item_key: itemKey,
        is_deferred: isDeferred,
      }),
    );
  };

  const handleValidateClick = () => {
    createFileValidatorLog();
  };

  const getResult = () => {
    if (file.is_deferred) {
      return 'Document deferred - to be gathered/provided later';
    }

    if (file.ai_result) return file.ai_result;

    if (file.status === 'approved' && file.files.length > 1) {
      return 'Files have been approved';
    }

    return 'File has been approved';
  };

  const isValidating = isPendingValidatorLog || file.status === 'validating';

  const showAlert =
    !isValidating &&
    (file.is_deferred || ['approved', 'failed'].includes(file.status));

  const isRestrictedByDeferralRule =
    !isDeferralAllowed(
      validationId,
      validations,
      isOwnershipOnlyDeferralEnabled,
    ) ||
    (isOwnershipOnlyDeferralEnabled &&
      formStateKey === 'autoPersonalForm' &&
      !OWNERSHIP_DEFERRABLE_VALIDATIONS.includes(validationId));

  const canDeferDocument =
    (isRestrictedByDeferralRule
      ? hasUnderwritingApproval || !!file.is_deferred
      : !file.mandatory || validationId === '097') &&
    !isMandatoryValidation &&
    !isBinderDisabled &&
    file.files.length === 0;

  return (
    <Alert status={getAlertStatus(file)} mt="10px">
      <VStack spacing="10px" w="100%" align="stretch">
        <Text fontSize="sm" fontWeight="medium">
          {`Upload for "${file.text}"`}
        </Text>
        {isMarketApprovalEmailUpload && (
          <Text fontSize="sm" color="gray.600">
            Accepted formats: .eml or .msg only
          </Text>
        )}

        {file.files.map((f) => (
          <Flex
            key={f.id}
            background="white"
            alignItems="center"
            p="10px"
            gap="10px"
            borderRadius={'md'}
          >
            {!isBinderDisabled && (
              <Button
                onClick={() => onFileRemove(f.id)}
                _hover={{ bg: 'red.500', color: 'white' }}
                size="sm"
                disabled={isValidating || isUploadingFile}
              >
                <DeleteIcon />
              </Button>
            )}

            <Link
              href={f.url}
              isExternal
              textDecoration="underline"
              fontSize="sm"
              fontWeight="semibold"
              color="teal"
            >
              {f.name}
            </Link>
          </Flex>
        ))}

        {uploadingFileError && (
          <Alert status="error" size="sm">
            <AlertIcon />
            Upload failed. Please try again.
          </Alert>
        )}

        <Input
          ref={fileInputRef}
          accept={acceptString}
          type="file"
          onChange={handleFileSelect}
          display="none"
          disabled={isBinderDisabled}
        />

        {!isValidating && !file.is_deferred && (
          <Button
            leftIcon={<AttachmentIcon />}
            onClick={handleUploadClick}
            isLoading={isUploadingFile}
            loadingText="Uploading..."
            size="sm"
            variant="outline"
            colorScheme="blue"
            background="white"
            disabled={isBinderDisabled || isUploadingFile}
          >
            Add file
          </Button>
        )}

        {canDeferDocument && (
          <Box
            p={3}
            borderRadius="md"
            bg={file.is_deferred ? 'blue.50' : 'gray.50'}
            borderWidth="1px"
            borderColor={file.is_deferred ? 'blue.200' : 'gray.200'}
          >
            <Checkbox
              isChecked={file.is_deferred || false}
              onChange={handleDeferredChange}
              size="md"
              colorScheme="blue"
              disabled={isBinderDisabled || isValidating}
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                Document to be gathered/provided later
              </Text>
            </Checkbox>
          </Box>
        )}

        {showAlert && (
          <Box
            border="1px solid"
            borderColor="gray.50"
            borderRadius="md"
            background="white"
            p={2}
          >
            {getResult()}
          </Box>
        )}

        {file.status !== 'approved' && !file.is_deferred && (
          <Button
            onClick={handleValidateClick}
            isDisabled={
              isUploadingFile || isValidating || file.files.length === 0
            }
            variant="brand"
            size="sm"
          >
            {isValidating ? <Spinner size="sm" mr={2} /> : null}
            Validate accuracy
          </Button>
        )}
      </VStack>
    </Alert>
  );
};

export default ValidateFile;
